import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Users, Award, Briefcase, Target, Zap, TrendingUp, X } from 'lucide-react';

/** ---- Floating Particles Component ---- */
const FloatingParticles = () => {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 2,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full bg-purple-400/30 blur-sm"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            left: `${p.x}%`,
            top: `${p.y}%`,
            animation: `float ${p.duration}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
          50% { transform: translate(20px, -30px) scale(1.2); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

/** ---- Animated Counter Component ---- */
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration]);

  return <span>{count.toLocaleString()}{suffix}</span>;
};

/** ---- Minimal local Typewriter component ---- */
const Typewriter = ({
  words = [],
  loop = true,
  typeSpeed = 75,
  deleteSpeed = 50,
  cursor = "_",
}) => {
  const [wordIndex, setWordIndex] = useState(0);
  const [display, setDisplay] = useState("");
  const [deleting, setDeleting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const current = words[wordIndex] || "";
    const isDone = !loop && wordIndex >= words.length;
    if (isDone) return;

    const tick = () => {
      const nextLen = deleting ? display.length - 1 : display.length + 1;
      const nextText = current.slice(0, Math.max(0, Math.min(current.length, nextLen)));
      setDisplay(nextText);

      if (!deleting && nextText === current) {
        setTimeout(() => setDeleting(true), 700);
      } else if (deleting && nextText === "") {
        setDeleting(false);
        setWordIndex((i) => (i + 1) % (words.length || 1));
      }
    };

    const delay = deleting ? deleteSpeed : typeSpeed;
    timerRef.current = setTimeout(tick, delay);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [display, deleting, wordIndex, words, loop, typeSpeed, deleteSpeed]);

  useEffect(() => {
    setDisplay("");
    setDeleting(false);
  }, [wordIndex]);

  return (
    <span>
      {display}
      <span className="ml-1 opacity-80 animate-pulse">{cursor}</span>
    </span>
  );
};

export function Section() {
  const navigate = useNavigate();
  const [showPathSelection, setShowPathSelection] = useState(false);

  const featurePills = [
    { icon: Briefcase, text: "AI Resume Builder", color: "from-purple-500 to-pink-500" },
    { icon: Target, text: "Job Matching", color: "from-blue-500 to-cyan-500" },
    { icon: Sparkles, text: "Interview Prep", color: "from-yellow-500 to-orange-500" },
  ];

  const stats = [
    { icon: Users, value: 10000, suffix: "+", label: "Active Users" },
    { icon: Award, value: 95, suffix: "%", label: "Success Rate" },
    { icon: Sparkles, value: 20, suffix: "+", label: "AI Features" },
  ];

  return (
    <section className="relative flex flex-col items-center text-center min-h-screen px-4 pt-5 md:pt-10 pb-5 overflow-hidden">
      {/* Animated Gradient Mesh Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#06010a] via-[#260345] to-[#3d0066]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-900/20 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-pink-900/20 via-transparent to-transparent"></div>
      </div>

      {/* Floating Particles */}
      <FloatingParticles />

      {/* Main Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto w-full">

        {/* Feature Pills - Floating Above Title */}
        <div className="flex flex-wrap justify-center gap-3 mb-8 animate-fade-in">
          {featurePills.map((pill, idx) => (
            <div
              key={idx}
              className="group relative px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 cursor-pointer"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              <div className={`flex items-center gap-2 text-sm font-medium text-white`}>
                <pill.icon className="w-4 h-4" />
                <span>{pill.text}</span>
              </div>
              {/* Glow effect on hover */}
              <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${pill.color} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300`}></div>
            </div>
          ))}
        </div>

        {/* Main Heading with Glow */}
        <div className="relative mb-6">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
            Let AI refine your{" "}
            <span className="relative inline-block">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 font-extrabold animate-gradient-x">
                <Typewriter
                  words={["{Career Path}", "{Resume}", "{Future}"]}
                  loop={true}
                  typeSpeed={85}
                  deleteSpeed={70}
                  cursor={"_"}
                  cursorSpeed={1000}
                />
              </span>
              {/* Glow effect behind text */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 blur-2xl opacity-30 -z-10"></div>
            </span>
          </h1>
        </div>

        {/* Subtitle with better typography */}
        <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed font-light">
          Combine human insight with AI precision to craft your career story,
          practice with confidence, and achieve your professional goals.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <button
            onClick={() => setShowPathSelection(true)}
            className="group relative px-8 py-4 bg-gradient-to-r from-[#ffc403] to-[#ffb000] text-black font-bold rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-yellow-500/50"
          >
            <span className="relative z-10 flex items-center gap-2">
              Get Started Free
              <Zap className="w-5 h-5 group-hover:animate-bounce" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>

          <button
            onClick={() => navigate("/jobsuite")}
            className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-semibold rounded-2xl border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105"
          >
            Explore Features
          </button>
        </div>

        {/* Stats Cards - Glassmorphism */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="group relative p-6 rounded-2xl bg-white/5 backdrop-blur-lg border border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105 hover:-translate-y-1"
              style={{ animationDelay: `${idx * 0.15}s` }}
            >
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"></div>

              <div className="relative z-10">
                <stat.icon className="w-8 h-8 text-purple-400 mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
                <div className="text-3xl font-bold text-white mb-1">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Mascot with enhanced styling */}
        <div className="relative inline-block">
          <img
            src={require("../../img/mascot.png")}
            alt="Mascot"
            className="w-48 md:w-56 mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300"
            style={{
              animation: "float-gentle 3s ease-in-out infinite",
              filter: "drop-shadow(0 20px 40px rgba(168, 85, 247, 0.4))"
            }}
          />
          {/* Glow ring around mascot */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-20 blur-3xl animate-pulse"></div>
        </div>

        <style>{`
          @keyframes float-gentle {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes gradient-x {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient-x {
            background-size: 200% 200%;
            animation: gradient-x 3s ease infinite;
          }
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 0.6s ease-out forwards;
          }
        `}</style>
      </div>

      {/* Path Selection Popup */}
      {showPathSelection && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="relative bg-[#1a0b2e] border border-white/20 p-8 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
            <div className="absolute top-0 right-0 p-4">
               <button onClick={() => setShowPathSelection(false)} className="text-white/50 hover:text-white">
                  <X className="w-6 h-6" />
               </button>
            </div>
            
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 text-center">Choose Your Goal</h3>
            <p className="text-gray-400 mb-8 text-center">Select the path that matches your current objective</p>
            
            <div className="flex flex-col gap-4">
              <button
                onClick={() => window.location.href = "https://job.novistep.com/auth"}
                className="group relative flex items-center justify-between p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/20 rounded-xl text-purple-400 group-hover:scale-110 transition-transform">
                    <Briefcase className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <div className="text-lg font-bold text-white">JobStep</div>
                    <div className="text-sm text-gray-400">Launch your career</div>
                  </div>
                </div>
                <Zap className="w-5 h-5 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>

              <button
                onClick={() => window.location.href = "https://study.novistep.com"}
                className="group relative flex items-center justify-between p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/20 rounded-xl text-blue-400 group-hover:scale-110 transition-transform">
                    <Target className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <div className="text-lg font-bold text-white">StudyStep</div>
                    <div className="text-sm text-gray-400">Master your skills</div>
                  </div>
                </div>
                <Zap className="w-5 h-5 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#260345] to-transparent pointer-events-none"></div>
    </section>
  );
}